import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Box,
  ModalFooter,
  Text,
  Flex
} from '@chakra-ui/react'
import { FC, useState } from 'react'
import { FaTruck } from 'react-icons/fa'
import { IoIosAddCircle } from 'react-icons/io'

import useParametrizationConfigPurchase from '@hook/parametrization/use-config-purchase'
import useFilters from '@hook/shared/use-filters'

import Filter from '@component/Shared/Filter'

import LotesSemRecomendacaoFields, {
  buildLotesSemRecomendacaoPayload,
  validateFaixaPercentual,
  validateLotesSemRecomendacao
} from './LotesSemRecomendacaoFields'

// Tipagem
interface IValue {
  label: string
  value: string
}

const ModalCreateAlcada: FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [usuario, setUsuario] = useState<IValue>(null)
  const { data } = useFilters()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [itensSemRecomendacao, setItensSemRecomendacao] = useState(false)
  const [lotesDe, setLotesDe] = useState('')
  const [lotesAte, setLotesAte] = useState('')
  const { createAlcadas, companieId } = useParametrizationConfigPurchase()

  const [errors, setErrors] = useState<string[]>([])

  const validate = () => {
    const newErrors: string[] = []
    if (usuario === null) {
      newErrors.push('Selecione um usuário.')
    }
    newErrors.push(...validateFaixaPercentual(startDate, endDate))
    newErrors.push(
      ...validateLotesSemRecomendacao(itensSemRecomendacao, lotesDe, lotesAte)
    )
    setErrors(newErrors)
    return newErrors.length === 0
  }

  const clearForm = () => {
    setUsuario(null)
    setStartDate('')
    setEndDate('')
    setItensSemRecomendacao(false)
    setLotesDe('')
    setLotesAte('')
    setErrors([])
  }

  const [isSaving, setIsSaving] = useState(false)

  const handleSubmit = async () => {
    if (isSaving) return
    if (validate()) {
      setIsSaving(true)
      const success = await createAlcadas({
        empresaId: companieId,
        minimo: startDate,
        maximo: endDate,
        usuarioAprovadorId: usuario.value,
        ...buildLotesSemRecomendacaoPayload(
          itensSemRecomendacao,
          lotesDe,
          lotesAte
        )
      })
      setIsSaving(false)
      // Fecha só no sucesso; no erro o toast explica e o formulário continua preenchido
      if (success !== false) {
        onClose()
        clearForm()
      }
    }
  }

  return (
    <>
      <Button
        fontSize={14}
        size="sm"
        leftIcon={<IoIosAddCircle />}
        colorScheme="blue"
        variant="outline"
        onClick={onOpen}
      >
        Adicionar
      </Button>

      <Modal
        isCentered
        isOpen={isOpen}
        onClose={() => {
          onClose()
          clearForm()
        }}
        scrollBehavior="inside"
        motionPreset="slideInBottom"
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            paddingTop={2}
            paddingBottom={2}
            paddingLeft={4}
            paddingRight={4}
            textColor={'gray.200'}
            background={'#1d5840'}
            borderTopLeftRadius={5}
            borderTopRightRadius={5}
            borderBottom="3px solid #ECC94B"
          >
            <Flex>
              <Box p={2}>
                <FaTruck />
              </Box>
              <Box p={2} pt={1}>
                Criar Alcadas Cronograma
              </Box>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody bg="white" pt={0} py={4}>
            <FormControl isRequired mb={4}>
              <Filter
                label="Usuário"
                options={data?.users}
                isMulti={false}
                value={[usuario]}
                onChange={value => setUsuario(value as IValue)}
              />
            </FormControl>

            <FormControl isRequired mb={4}>
              <FormLabel>De (%)</FormLabel>
              <Input
                type="number"
                min={0}
                borderColor="gray.400"
                _hover={{ borderColor: 'gray.600' }}
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired mb={4}>
              <FormLabel>Até (%)</FormLabel>
              <Input
                type="number"
                min={0}
                borderColor="gray.400"
                _hover={{ borderColor: 'gray.600' }}
                border="1px solid"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </FormControl>

            <LotesSemRecomendacaoFields
              itensSemRecomendacao={itensSemRecomendacao}
              lotesDe={lotesDe}
              lotesAte={lotesAte}
              onToggle={setItensSemRecomendacao}
              onLotesDeChange={setLotesDe}
              onLotesAteChange={setLotesAte}
            />

            {/* Mensagens de Erro */}
            {errors.length > 0 && (
              <Box mt={2}>
                {errors.map((err, idx) => (
                  <Text key={idx} color="red.500" fontSize="sm">
                    {err}
                  </Text>
                ))}
              </Box>
            )}
          </ModalBody>

          <ModalFooter
            bg="white"
            borderBottomLeftRadius={5}
            borderBottomRightRadius={5}
          >
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                onClose()
                clearForm()
              }}
            >
              Fechar
            </Button>
            <Button
              bg="green.600"
              color="#fff"
              onClick={handleSubmit}
              isLoading={isSaving}
              _hover={{ bg: 'green.500' }}
              _active={{ bg: 'green.500' }}
            >
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ModalCreateAlcada
